import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield } from "lucide-react";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { useAuth } from "../../App";
import { WinnerBubble } from "../WinnerBubble";
import { JackpotWinnerModal } from "../JackpotWinnerModal";
import GiveawayWinnerBanner from "../GiveawayWinnerBanner";

const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { fetchUser, token, user } = useAuth();

  const isModerator = user?.is_moderator || user?.role === "moderator";
  const isAdmin = user?.is_admin || user?.email === "admin@jackroyals.com" || user?.role === "admin";
  const isSeniorMod = user?.is_senior_moderator || user?.role === "senior_moderator";

  // Instant refresh on tab focus / visibility change (polling is handled in App.js)
  useEffect(() => {
    if (!token) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchUser();
    };
    const onFocus = () => fetchUser();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen arcade-bg text-white overflow-x-hidden">
      {/* Fixed arcade background */}
      <div className="fixed inset-0 z-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at top, #151b3a 0%, #050814 50%, #02030a 100%)' }} />
      {/* Subtle grid pattern overlay */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(77,124,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(77,124,255,0.3) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      {/* Neon ambient glow overlay */}
      <div className="arcade-glow-overlay" />
      {/* Global Winner Announcement Bubble */}
      <WinnerBubble />
      {/* Personal Jackpot Winner Fireworks Modal (for the winner only) */}
      <JackpotWinnerModal />
      {/* Giveaway Winner Congratulations Modal */}
      <GiveawayWinnerBanner />
      <div className="flex relative z-[1]">
        {/* Sidebar */}
        <Sidebar 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
        />
        
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-screen w-full max-w-full overflow-x-hidden">
          {/* Header */}
          <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} onCloseSidebar={() => setSidebarOpen(false)} />
          
          {/* Mod / Admin staff banner — visible to mods, senior mods & admins only, never to regular users */}
          {(isModerator || isAdmin || isSeniorMod) && (
            <Link
              to={isAdmin || isSeniorMod ? "/admin" : "/moderator"}
              className="block w-full bg-gradient-to-r from-emerald-600/30 via-emerald-500/20 to-emerald-600/30 border-y border-emerald-500/40 hover:bg-emerald-500/25 transition-colors group"
              data-testid="staff-banner-mod-link"
            >
              <div className="px-3 sm:px-4 py-2 flex items-center justify-center gap-2 text-emerald-200 text-xs sm:text-sm font-semibold">
                <Shield className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span>
                  {isAdmin ? "ADMIN VIEW" : isSeniorMod ? "SENIOR MOD" : "MODERATOR"} · Click here to open your{" "}
                  <span className="underline">
                    {isAdmin || isSeniorMod ? "Admin Dashboard" : "Moderator Dashboard"}
                  </span>
                </span>
                <span className="text-emerald-300 group-hover:translate-x-0.5 transition-transform">→</span>
              </div>
            </Link>
          )}

          {/* Subtle accent line under header */}
          <div className="w-full h-[1px]" style={{ background: 'linear-gradient(to right, transparent, rgba(77,124,255,0.3), transparent)' }} />
          
          {/* Page Content - pb-20 on mobile for bottom nav clearance.
              NOTE: NO `overflow-y-auto` here — nested scroll containers break mobile
              Safari/Chrome touch-scroll and cause the page to render at the wrong
              zoom level. Let the page body scroll naturally. */}
          <main className="flex-1 w-full max-w-full overflow-x-hidden pb-20 md:pb-0">
            {children}
          </main>

          {/* Mobile-only tiny build version stamp — lets the user confirm at
              a glance that their phone is actually serving the latest bundle
              after a deploy. If you ever see an OLDER version here than the
              one we shipped, you have a stale cache and need to clear it. */}
          <div className="md:hidden text-center text-[9px] text-gray-700 pb-24 -mt-4" data-testid="build-version-mobile">
            JackRoyals · <span className="text-gray-600">v2.5</span>
          </div>
          
          {/* Footer - 18+ Disclaimer - hidden on mobile */}
          <footer className="hidden md:block border-t py-4 px-4" style={{ background: '#050814', borderColor: '#1f2a4d' }}>
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
                <span className="inline-flex items-center justify-center text-white text-xs font-bold px-2 py-1 rounded" style={{ background: '#4d7cff' }}>
                  18+
                </span>
                <p className="text-gray-400 text-xs sm:text-sm">
                  Play Responsibly. JackRoyals is a sweepstakes entertainment platform. No purchase necessary.
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-4 mt-3 text-xs text-gray-500">
                <a href="/terms" className="hover:text-[#4d7cff] transition-colors">Terms of Service</a>
                <span className="hidden sm:inline" style={{ color: '#1f2a4d' }}>|</span>
                <a href="/privacy" className="hover:text-[#4d7cff] transition-colors">Privacy Policy</a>
                <span className="hidden sm:inline" style={{ color: '#1f2a4d' }}>|</span>
                <a href="/responsible-gaming" className="hover:text-[#4d7cff] transition-colors">Responsible Gaming</a>
                <span className="hidden sm:inline" style={{ color: '#1f2a4d' }}>|</span>
                <a href="/sweepstakes-rules" className="hover:text-[#4d7cff] transition-colors">Sweepstakes Rules</a>
              </div>
              {/* Facebook Community CTA */}
              <div className="flex items-center justify-center mt-4">
                <a
                  href="https://www.facebook.com/share/g/1AqUU8BF6w/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-fb-community"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-white transition-all hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #1877f2, #0a5dc2)', boxShadow: '0 4px 16px rgba(24,119,242,0.35)' }}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.78-3.91 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.91h-2.34V22c4.78-.76 8.43-4.92 8.43-9.94z"/>
                  </svg>
                  Join our Facebook Community
                </a>
              </div>
              <p className="text-center text-gray-600 text-xs mt-3">
                &copy; {new Date().getFullYear()} JackRoyals. All rights reserved. <span className="text-gray-700 ml-2" data-testid="build-version">v2.6</span>
              </p>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default MainLayout;
